/** Edit a committed chip in place: a popover anchored to the chip with two steps.
 *
 * - **Operator** — the namespace's operators (the creation menu's list); the namespace
 *   itself is fixed. Picking another operator clears the value, because the operator
 *   defines what the value means; picking the chip's own operator back restores it.
 * - **Value** (the step it opens on) — exactly the editor creation uses (ValueEditor:
 *   the TimeWindowPanel, the date pickers, the suggestion list) plus a text input where
 *   creation types into the search box itself.
 *
 * The edit reaches the chip only on commit (a suggestion, a preset, Apply, Enter); Esc
 * or a click away leaves the chip as it was. A free-text chip has the Value step only.
 */
import CheckIcon from '@mui/icons-material/Check';
import {
  Box,
  Button,
  Divider,
  InputBase,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useState, type ReactNode } from 'react';

import { condDisplay, displayValue } from './chipLabel';
import type { ChipToken } from './chips';
import { opLabel } from './OperatorMenu';
import { unquoteIfWrapped } from './serializer';
import { useSuggestions } from './useSuggestions';
import { ValueEditor } from './ValueEditor';
import { chipOperator, offeredOperators, parseTypedValue, valueToText } from './valueStage';
import type { FilterValue, NamespaceConfigMap, Suggestion } from './types';

export type EditableChip = Extract<ChipToken, { t: 'cond' } | { t: 'word' }>;

interface Props {
  anchorEl: HTMLElement | null;
  chip: EditableChip;
  namespaces: NamespaceConfigMap;
  extraFilterValues?: Record<string, string>;
  onCommit: (chip: ChipToken) => void;
  onClose: () => void;
}

type Step = 'operator' | 'value';

/** The value drafts, by editor kind: typed text, or the two date bounds. */
interface Draft {
  text: string;
  from: string;
  to: string;
}

function draftFor(op: string, value: FilterValue | null): Draft {
  if (value === null) return { text: '', from: '', to: '' };
  if (Array.isArray(value)) return { text: valueToText(op, value), from: value[0], to: value[1] };
  return { text: value, from: '', to: value };
}

export function ChipEditor({
  anchorEl,
  chip,
  namespaces,
  extraFilterValues,
  onCommit,
  onClose,
}: Props) {
  const cfg = chip.t === 'cond' ? namespaces[chip.namespace] : undefined;
  const initialOp = chip.t === 'cond' && cfg ? chipOperator(chip.operator, cfg.operators) : '';
  const initialValue: FilterValue = chip.t === 'cond' ? chip.value : chip.text;

  const [step, setStep] = useState<Step>('value');
  const [op, setOp] = useState(initialOp);
  const [draft, setDraft] = useState<Draft>(() => draftFor(initialOp, initialValue));
  const [scope, setScope] = useState<'all' | undefined>(chip.t === 'cond' ? chip.scope : undefined);
  // -1 until the user moves through the list or edits the text: Enter on an untouched
  // value re-commits it rather than swapping it for the top suggestion.
  const [highlight, setHighlight] = useState(-1);
  const opChanged = op !== initialOp;

  const operators = cfg ? offeredOperators(cfg, initialOp) : [];
  const kind: 'window' | 'date' | 'text' =
    cfg?.valueType === 'datetime' && op === 'range'
      ? 'window'
      : cfg?.valueType === 'date' || cfg?.valueType === 'datetime'
        ? 'date'
        : 'text';

  const fetcher = kind === 'text' ? cfg?.fetchSuggestions : undefined;
  const { suggestions, loading } = useSuggestions(fetcher, draft.text, {
    ...(cfg?.extraFilterParams ?? {}),
    ...(extraFilterValues ?? {}),
  });

  const commitCond = (nextOp: string, value: FilterValue) => {
    if (chip.t !== 'cond') return;
    const scoped = scope === 'all' && cfg?.allAny ? { scope: 'all' as const } : {};
    onCommit({ t: 'cond', namespace: chip.namespace, operator: nextOp, value, ...scoped });
  };

  const pickOperator = (next: string) => {
    if (next !== op) {
      setOp(next);
      // Back to the chip's own operator restores its value; any other starts empty.
      setDraft(draftFor(next, next === initialOp ? initialValue : null));
      setHighlight(-1);
    }
    setStep('value');
  };

  const pickSuggestion = (s: Suggestion) => {
    // Under match-by-ID the value is the record's id, not its name.
    if (op === 'idmatch') {
      if (s.id != null) commitCond('idmatch', String(s.id));
      return;
    }
    commitCond(op, s.value);
  };

  const typed = chip.t === 'word' ? null : parseTypedValue(op, draft.text, cfg?.operators ?? []);
  const canApply =
    chip.t === 'word'
      ? draft.text.trim() !== ''
      : kind === 'date'
        ? op === 'range'
          ? draft.from !== '' && draft.to !== ''
          : draft.to !== ''
        : typed !== null;

  const apply = () => {
    if (chip.t === 'word') {
      const text = unquoteIfWrapped(draft.text.trim());
      if (text) onCommit({ t: 'word', text });
      return;
    }
    if (kind === 'text' && highlight >= 0 && suggestions[highlight]) {
      pickSuggestion(suggestions[highlight]!);
      return;
    }
    if (kind === 'date') {
      if (canApply) commitCond(op, op === 'range' ? [draft.from, draft.to] : draft.to);
      return;
    }
    if (typed) commitCond(typed.op, typed.value);
  };

  const valueSummary =
    chip.t === 'word' ? displayValue(chip.text) : opChanged ? '—' : condDisplay(chip, cfg).text;

  let body: ReactNode;
  if (step === 'operator') {
    body = (
      <List dense disablePadding aria-label="Operators" sx={{ py: 0.5 }}>
        {operators.map((o) => (
          <ListItemButton
            key={o}
            selected={o === op}
            onClick={() => pickOperator(o)}
            sx={{ borderRadius: 1, mx: 1 }}
          >
            <ListItemIcon sx={{ minWidth: 28 }}>
              {o === op && <CheckIcon fontSize="small" color="primary" />}
            </ListItemIcon>
            <ListItemText primary={opLabel(o)} secondary={o} />
          </ListItemButton>
        ))}
      </List>
    );
  } else {
    const valueEditor = cfg && (
      <ValueEditor
        anchorEl={null}
        inline
        config={cfg}
        operator={op}
        query={kind === 'text' ? draft.text : draft.to}
        first={kind === 'text' ? undefined : draft.from}
        suggestions={suggestions}
        loading={loading}
        highlightedIndex={highlight}
        onHighlightChange={setHighlight}
        onSelectSuggestion={(v) => {
          const s = suggestions.find((x) => x.value === v);
          if (s) pickSuggestion(s);
          else commitCond(op, v);
        }}
        onSelectSuggestionById={(s) => {
          if (s.id != null) commitCond('idmatch', String(s.id));
        }}
        onSelectDate={(v, bound) =>
          setDraft((d) => (bound === 'from' ? { ...d, from: v } : { ...d, to: v }))
        }
        onSelectRange={(from, to) => commitCond('range', [from, to])}
      />
    );
    body = (
      <Box sx={{ px: 2, py: 1.5 }}>
        {opChanged && (
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>
            New operator — enter a value.
          </Typography>
        )}
        {cfg?.allAny && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={scope === 'all' ? 'all' : 'any'}
            onChange={(_, v: 'any' | 'all' | null) =>
              v && setScope(v === 'all' ? 'all' : undefined)
            }
            aria-label="booking match scope"
            sx={{ mb: 1, '& .MuiToggleButton-root': { py: 0, px: 1, fontSize: 12 } }}
          >
            <ToggleButton value="any" aria-label="any booking matches">
              any booking
            </ToggleButton>
            <ToggleButton value="all" aria-label="all bookings match">
              all bookings
            </ToggleButton>
          </ToggleButtonGroup>
        )}
        {kind === 'text' && (
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              apply();
            }}
            sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
          >
            <InputBase
              autoFocus
              value={draft.text}
              onChange={(e) => {
                setDraft((d) => ({ ...d, text: e.target.value }));
                setHighlight(0);
              }}
              onFocus={(e) => e.target.select()}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown' && suggestions.length > 0) {
                  e.preventDefault();
                  setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
                } else if (e.key === 'ArrowUp' && suggestions.length > 0) {
                  e.preventDefault();
                  setHighlight((h) => Math.max(h - 1, -1));
                }
              }}
              placeholder={op === 'range' ? 'from..to' : 'value'}
              inputProps={{ 'aria-label': 'value', spellCheck: false }}
              sx={{
                flex: 1,
                fontSize: 14,
                px: 1.25,
                height: 36,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                '&.Mui-focused': {
                  borderColor: 'primary.main',
                  boxShadow: (t) => `0 0 0 1px ${t.palette.primary.main}`,
                },
              }}
            />
            <Button
              type="submit"
              variant="contained"
              size="small"
              disabled={!canApply && highlight < 0}
            >
              Apply
            </Button>
          </Box>
        )}
        {chip.t === 'cond' &&
          kind === 'text' &&
          op === 'range' &&
          draft.text.trim() !== '' &&
          typed === null && (
            <Typography variant="caption" color="error" component="p" sx={{ mt: 0.5 }}>
              Write a range as from..to
            </Typography>
          )}
        {valueEditor && <Box sx={{ mt: kind === 'text' ? 1 : 0 }}>{valueEditor}</Box>}
        {kind === 'date' && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button variant="contained" size="small" disabled={!canApply} onClick={apply}>
              Apply
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{ paper: { sx: { mt: 0.5, minWidth: 380, maxWidth: 'calc(100vw - 32px)' } } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, px: 2, pt: 1 }}>
        <Typography
          variant="subtitle2"
          component="h2"
          sx={{ fontWeight: 700, pb: 1.25, whiteSpace: 'nowrap' }}
        >
          {chip.t === 'cond' ? chip.namespace : 'Free text'}
        </Typography>
        <Tabs
          value={step}
          onChange={(_, v: Step) => setStep(v)}
          aria-label="Edit filter"
          sx={{
            minHeight: 44,
            // The selected step is underlined by its own border: MUI's measured
            // indicator lands mid-grow inside a popover.
            '& .MuiTabs-indicator': { display: 'none' },
            '& .MuiTab-root': { minHeight: 44, py: 0.5, px: 1.25, alignItems: 'flex-start' },
            '& .MuiTab-root.Mui-selected': {
              boxShadow: (t) => `inset 0 -2px 0 ${t.palette.primary.main}`,
            },
          }}
        >
          <Tab
            value="operator"
            disabled={operators.length < 2}
            label={<StepLabel caption="Operator" text={chip.t === 'word' ? 'none' : opLabel(op)} />}
            title={chip.t === 'word' ? 'Free text has no operator' : undefined}
          />
          <Tab value="value" label={<StepLabel caption="Value" text={valueSummary} />} />
        </Tabs>
      </Box>
      <Divider />
      {body}
    </Popover>
  );
}

function StepLabel({ caption, text }: { caption: string; text: string }) {
  return (
    <Box sx={{ textAlign: 'left', textTransform: 'none', maxWidth: 220 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        component="span"
        sx={{ display: 'block', lineHeight: 1.3 }}
      >
        {caption}
      </Typography>
      <Typography
        variant="body2"
        component="span"
        sx={{
          display: 'block',
          fontWeight: 600,
          color: 'text.primary',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {text}
      </Typography>
    </Box>
  );
}
